import { computed } from 'vue';
import { get } from 'lodash';

import { NovaDate } from '@nova/core';

import type { YardTaskTimelineEvent } from '@satellite/composables/useYardTaskTimeline';

export interface UseYardTaskEventOptions {
  timezone: string;
  locale?: string;
}

/**
 * @param {YardTaskTimelineEvent} event
 * @param {UseYardTaskEventOptions} options
 */
export default function useYardTaskEvent(
  event: YardTaskTimelineEvent,
  options: UseYardTaskEventOptions
) {
  const { timezone, locale } = options || {};

  const formatDate = (date: string | null, format: string = 'MMM d, yyyy') => {
    if (!date) {
      return null;
    }

    try {
      return new NovaDate(date).format(format, { tz: timezone, locale });
    } catch (error) {
      console.error('Error formatting event date:', error);
      return null;
    }
  };

  const formatTime = (date: string | null, format: string = 'h:mm a') => {
    if (!date) {
      return null;
    }

    try {
      return new NovaDate(date).format(format, { tz: timezone, locale });
    } catch (error) {
      console.error('Error formatting event time:', error);
      return null;
    }
  };

  const user = computed(() => {
    const user = get(event, 'data.data.user');
    return user || null;
  });

  const createDateTime = computed(() => {
    const createDateTime = get(event, 'data.createDateTime');

    if (!createDateTime) {
      return null;
    }

    return createDateTime;
  });

  const formattedCreatedDate = computed(() => {
    return formatDate(createDateTime.value);
  });

  const formattedCreatedTime = computed(() => {
    return formatTime(createDateTime.value);
  });

  const eventType = computed(() => {
    return get(event, 'data.event') || '';
  });

  return {
    user,
    eventType,
    formattedCreatedDate,
    formattedCreatedTime
  };
}
